import { useTranslation } from 'react-i18next';
import { ModalCloseButton } from '@/shared/components/ModalCloseButton';
import { DialogShell } from '@/shared/components/DialogShell';
import checkIconCircle from '@/assets/svg/check_icon_circle.svg';
import warningIcon from '@/assets/svg/warning_icon.svg';

interface WorkConfirmDialogsProps {
  showPublishConfirmation: boolean;
  showPublishSuccess: boolean;
  showDeleteConfirmation: boolean;
  showUnsavedConfirm: boolean;
  deleting: boolean;
  onCancelPublish: () => void;
  onConfirmPublish: () => void;
  onCloseSuccess: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onContinueEditing: () => void;
  onDiscardChanges: () => void;
}

export const WorkConfirmDialogs: React.FC<WorkConfirmDialogsProps> = ({
  showPublishConfirmation,
  showPublishSuccess,
  showDeleteConfirmation,
  showUnsavedConfirm,
  deleting,
  onCancelPublish,
  onConfirmPublish,
  onCloseSuccess,
  onCancelDelete,
  onConfirmDelete,
  onContinueEditing,
  onDiscardChanges,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <DialogShell
        isOpen={showPublishConfirmation}
        onClose={onCancelPublish}
        titleId="work-publish-confirm-title"
        overlayClassName="publish-confirmation-overlay"
        dialogClassName="publish-confirmation-modal"
      >
        <ModalCloseButton onClick={onCancelPublish} className="publish-modal-close" />
        <h2 id="work-publish-confirm-title" className="publish-confirmation-title">
          {t('exhibitions.publishConfirmationTitle')}
        </h2>
        <button type="button" className="publish-confirm-button" onClick={onConfirmPublish}>
          {t('exhibitions.publishConfirmButton')}
        </button>
        <button type="button" className="publish-cancel-link" onClick={onCancelPublish}>
          {t('common.cancel')}
        </button>
      </DialogShell>

      <DialogShell
        isOpen={showPublishSuccess}
        onClose={onCloseSuccess}
        titleId="work-publish-success-title"
        overlayClassName="publish-success-overlay"
        dialogClassName="publish-success-modal"
      >
        <ModalCloseButton onClick={onCloseSuccess} className="publish-modal-close" />
        <div className="publish-success-icon">
          <img src={checkIconCircle} alt="" />
        </div>
        <h2 id="work-publish-success-title" className="publish-success-title">
          {t('exhibitions.publishSuccessTitle')}
        </h2>
        <button type="button" className="publish-close-link" onClick={onCloseSuccess}>
          {t('exhibitions.close')}
        </button>
      </DialogShell>

      <DialogShell
        isOpen={showDeleteConfirmation}
        onClose={onCancelDelete}
        titleId="work-delete-confirm-title"
        overlayClassName="publish-confirmation-overlay"
        dialogClassName="publish-confirmation-modal"
      >
        <ModalCloseButton onClick={onCancelDelete} className="publish-modal-close" />
        <h2 id="work-delete-confirm-title" className="publish-confirmation-title">
          {t('works.confirmDelete')}
        </h2>
        <button
          type="button"
          className="publish-confirm-button"
          onClick={onConfirmDelete}
          disabled={deleting}
        >
          {deleting ? t('common.loading') : t('works.deleteWork')}
        </button>
        <button
          type="button"
          className="publish-cancel-link"
          onClick={onCancelDelete}
          disabled={deleting}
        >
          {t('common.cancel')}
        </button>
      </DialogShell>

      <DialogShell
        isOpen={showUnsavedConfirm}
        onClose={onContinueEditing}
        titleId="work-unsaved-confirm-title"
        overlayClassName="unsaved-confirm-overlay"
        dialogClassName="unsaved-confirm-modal"
      >
        <ModalCloseButton onClick={onContinueEditing} className="unsaved-confirm-close" />
        <div className="unsaved-confirm-icon" aria-hidden>
          <img src={warningIcon} alt="" />
        </div>
        <h2 id="work-unsaved-confirm-title" className="unsaved-confirm-title">
          {t('exhibitions.unsavedChangesTitle')}
        </h2>
        <p className="unsaved-confirm-message">{t('exhibitions.unsavedChangesMessage')}</p>
        <button type="button" className="unsaved-confirm-continue-btn" onClick={onContinueEditing}>
          {t('exhibitions.continueEditing')}
        </button>
        <button type="button" className="unsaved-confirm-discard-link" onClick={onDiscardChanges}>
          {t('exhibitions.quitWithoutSaving')}
        </button>
      </DialogShell>
    </>
  );
};
